import { isPlainObject } from 'lodash';

type JsonReadableResponse<TResponse> = {
  json: () => Promise<TResponse>;
};

const hasJsonResponse = <TResponse>(response: unknown): response is JsonReadableResponse<TResponse> =>
  typeof (response as Partial<JsonReadableResponse<TResponse>> | null)?.json === 'function';

export const readJsonResponse = async <TResponse>(response: unknown): Promise<TResponse> => {
  if (hasJsonResponse<TResponse>(response)) {
    return response.json();
  }

  return response as TResponse;
};

export const readJsonResponseOrFallback = async <TResponse>(
  response: unknown,
  fallback: TResponse,
): Promise<TResponse> => {
  try {
    return await readJsonResponse<TResponse>(response);
  } catch (error) {
    return fallback;
  }
};

const getResponseBodyText = async (response: Response): Promise<string> => {
  try {
    return await response.clone().text();
  } catch (error) {
    return '';
  }
};

const parseJsonBody = (bodyText: string): Nullable<Record<string, unknown>> => {
  try {
    const data = JSON.parse(bodyText);

    return isPlainObject(data) ? (data as Record<string, unknown>) : null;
  } catch (error) {
    return null;
  }
};

const getErrorMessage = (data: Nullable<Record<string, unknown>>): string | undefined =>
  typeof data?.message === 'string' && data.message ? data.message : undefined;

const getErrorMessages = (data: Nullable<Record<string, unknown>>): string[] => {
  if (!Array.isArray(data?.errors)) return [];

  return data.errors.map(error => String(error)).filter(Boolean);
};

const getFallbackErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error && error.message) return error.message;

  return isPlainObject(error) && typeof (error as { message?: unknown }).message === 'string'
    ? (error as { message: string }).message
    : undefined;
};

export const parseSyberryTodayApiError = async (error: unknown): Promise<Error> => {
  if (!(error instanceof Response)) {
    return new Error(getFallbackErrorMessage(error) ?? 'Something went wrong');
  }

  const bodyText = await getResponseBodyText(error);
  const data = parseJsonBody(bodyText);
  const errors = getErrorMessages(data);
  const message = getErrorMessage(data) ?? (errors.length ? errors.join('\n') : bodyText);

  return new Error(message || 'Something went wrong');
};

export const withSyberryTodayApiError = async <TResponse>(request: () => Promise<TResponse>): Promise<TResponse> => {
  try {
    return await request();
  } catch (error) {
    throw await parseSyberryTodayApiError(error);
  }
};
